import { DateValue } from '@internationalized/date';
import { format, isAfter, isBefore, isSameMonth, isSameYear, parseISO, startOfDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useCallback, useMemo } from 'react';
import useTimeFormat from 'src/hooks/useTimeFormat';
import { SingleStudentDetails } from 'src/pages/single-student/singleStudent.types';

import { InvoiceDetails } from '../billing/billing.types';

type UseActivityDatePickerProps = {
  student?: SingleStudentDetails;
  month?: number;
  year?: number;
  enableOnlyProvidedMonthAndYear?: boolean;
};

const extractDateInTimezone = (
  dateString: string | undefined,
  timeZone: string,
): Date | undefined => {
  if (!dateString) return undefined;

  const utcDate = parseISO(dateString);
  const dateOnly = formatInTimeZone(utcDate, timeZone, 'yyyy-MM-dd');
  return startOfDay(parseISO(dateOnly));
};

const getDateRange = (student: SingleStudentDetails | undefined, timeZone: string) => {
  const startDateStr = student?.serviceStartDate || student?.startDate;
  const finishDateStr = student?.serviceFinishDate || student?.finishDate;

  return {
    startDate: startDateStr ? extractDateInTimezone(startDateStr, timeZone) : undefined,
    finishDate: finishDateStr ? extractDateInTimezone(finishDateStr, timeZone) : undefined,
  };
};

export const useActivityDatePicker = ({
  student,
  month,
  year,
  enableOnlyProvidedMonthAndYear,
}: UseActivityDatePickerProps) => {
  const { timeZone } = useTimeFormat();
  const { startDate, finishDate } = useMemo(
    () => getDateRange(student, timeZone),
    [student, timeZone],
  );

  const isDateDisabled = useCallback(
    (d: DateValue) => {
      const dDate = new Date(d.year, d.month - 1, d.day);
      if (enableOnlyProvidedMonthAndYear && (month || month === 0) && (year || year === 0)) {
        return (
          !isSameMonth(dDate, new Date(year, month - 1, 5)) ||
          !isSameYear(dDate, new Date(year, month - 1, 5))
        );
      }

      if (!student) {
        if (month !== undefined && year !== undefined) {
          const targetDate = new Date(year, month - 1, 1);
          return !isSameMonth(dDate, targetDate) || !isSameYear(dDate, targetDate);
        }
        return false;
      }

      if (startDate && isBefore(dDate, startDate)) {
        return true;
      }

      if (finishDate && isAfter(dDate, finishDate)) {
        return true;
      }

      return false;
    },
    [student, month, year, startDate, finishDate, enableOnlyProvidedMonthAndYear],
  );

  const initialDate = useMemo(() => {
    if (month !== undefined && year !== undefined) {
      return format(new Date(year, month - 1, 15), 'yyyy-MM-dd');
    }
    return undefined;
  }, [month, year]);

  return { isDateDisabled, initialDate };
};

export const useServiceDateRange = (invoice?: InvoiceDetails) => {
  const isDateDisabled = useCallback(
    (d: DateValue) => {
      const { startYear, endYear } = invoice?.schoolYear || {};
      if (!startYear || !endYear) {
        return true;
      }
      const dDate = new Date(d.year, d.month - 1, d.day);
      const rangeStart = new Date(startYear, 6, 1);
      const rangeEnd = new Date(endYear, 5, 30);
      return isBefore(dDate, rangeStart) || isAfter(dDate, rangeEnd);
    },
    [invoice],
  );

  return { isDateDisabled };
};
